import { apiErrorMessage } from '@agos/api-client'
import {
  getGetVatCategoriesQueryKey,
  getGetVatConfigurationQueryKey,
  useDeleteVatCategoriesId,
  useGetVatCategories,
  useGetVatConfiguration,
  usePatchVatCategoriesId,
  usePostVatCategories,
} from '@agos/api-client/menu'
import { usePatchSalesChannelsId } from '@agos/api-client/orders'
import {
  getGetSettingsFiskalQueryKey,
  usePutSettingsFiskalShared,
} from '@agos/api-client/fiskal'
import { T, formatDecimal, tt, useAuth } from '@agos/shared'
import {
  BUTTON_PRIMARY,
  BUTTON_SECONDARY,
  cn,
  ConfirmDialog,
  PageHeader,
  Spinner,
  StatusBadge,
  useToast,
} from '@agos/ui'
import { useQueryClient } from '@tanstack/react-query'
import { AlertCircle, Percent, Plus, RotateCcw, Save, Trash2, Truck, X } from 'lucide-react'
import { useMemo, useState } from 'react'

import { mayWrite, readOnlyReason } from '../write-access'
import {
  canSave,
  categoriesOf,
  defaultNote,
  deleteWarning,
  emptyForm,
  formOf,
  isBlocking,
  isPrintable,
  printabilityNote,
  problemMessage,
  problemsOf,
  slotDiscoveryNote,
  unprintableWarning,
  vatPayload,
  type VatCategory,
  type VatForm,
} from './vat-categories'
import { RegisterRatePicker } from './RegisterRatePicker'
import { rateOptions, type VatCategory as RateCategory } from './vat-rates'
import {
  mayClearSlot,
  registerNote,
  slotLabel,
  slotPayload,
  slotSource,
  unknownRateNote,
  vatConfigurationOf,
  type VatSlotChannel,
} from './vat-configuration'

/**
 * `/vat-categories` — §9.5's DPH administration. **A justified EXTRA** in the parity diff, citing that
 * section.
 *
 * The screen exists because of **FB-183**: two of the four rates this franchise charges have no confirmed
 * register index, so a receipt carrying either **fails at the till** — `FiskalReceiptService` refuses
 * rather than register the wrong tax. Nothing said so anywhere until now.
 *
 * The index is not guessable: it is whatever that device was configured with, and `FRINFO` is how you ask
 * it. So the screen names the problem and the command, and does not pretend to solve it.
 */

export function VatCategoriesPage() {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const writable = mayWrite('vat-categories', user?.systemRole)
  const readOnly = readOnlyReason('vat-categories', user?.systemRole)
  // §9.324, the owner's A13 - *"I want these DPHs to be merged together"*. One read answers the
  // register's fallback pair, every channel's slot and the products charging a rate this franchise
  // does not have; BACKEND.md §10A.113 says why those stay four stores behind one endpoint.
  const configurationQuery = useGetVatConfiguration()
  const setChannelSlot = usePatchSalesChannelsId()
  /**
   * §9.327 - the register's three rates, moved here from *Pokladňa → Nastavenia*.
   *
   * The owner asked twice for one place. §9.324 had kept the pickers there because moving them would
   * have orphaned the guard that catches a rate banding onto another letter; the guard moved too, so
   * this screen is the single writer and that page shows the values read-only.
   *
   * **A partial body.** `SharedSettingsRequest.applyTo` is null-guarded per field, so sending the
   * three rates alone leaves the restaurant's name, address, IČO and DIČ exactly as they were - and
   * they are on the same endpoint.
   */
  // No `useGetSettingsFiskal` here: `/vat/configuration` (§10A.113) already answers the register's
  // three rates, and a second read of the same values is a second thing to keep in step.
  const saveRates = usePutSettingsFiskalShared()
  const [rateDraft, setRateDraft] = useState<Record<string, string> | null>(null)

  const { data, isLoading } = useGetVatCategories()
  const categories = useMemo(() => categoriesOf(data), [data])

  const [editing, setEditing] = useState<{ id: number | null; form: VatForm } | null>(null)
  const [deleting, setDeleting] = useState<VatCategory | null>(null)

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: getGetVatCategoriesQueryKey() })
  }
  const onError = () => toast({ variant: 'destructive', title: tt('Zmena sa neuložila') })

  const create = usePostVatCategories({
    mutation: {
      onSuccess: () => {
        setEditing(null)
        refresh()
        toast({ title: tt('Sadzba pridaná') })
      },
      onError,
    },
  })
  const update = usePatchVatCategoriesId({
    mutation: {
      onSuccess: () => {
        setEditing(null)
        refresh()
        toast({ title: tt('Sadzba uložená') })
      },
      onError,
    },
  })
  const remove = useDeleteVatCategoriesId({
    mutation: {
      onSuccess: () => {
        setDeleting(null)
        refresh()
        toast({ title: tt('Sadzba zmazaná') })
      },
      onError: (caught) => {
        setDeleting(null)
        toast({
          variant: 'destructive',
          title: tt('Sadzba sa nedá zmazať'),
          description: apiErrorMessage(
                         caught,
                         tt('Server ju odmietol - pravdepodobne sa na ňu niečo účtuje. Vypnite ju.'),
                       ),
        })
      },
    },
  })

  /**
   * **Every hook above the `isLoading` return, and that is not a style choice.**
   *
   * These three `useMemo`s sat *below* it when §9.327 added them, so the loading render ran fewer
   * hooks than the loaded one and React threw #310 - *rendered more hooks than during the previous
   * render* - which blanks the whole screen. The build was clean and 19 e2e tests failed, including
   * every one that predates the change.
   */
  const configuration = vatConfigurationOf(configurationQuery.data)

  /**
   * §9.327 - the register's stored rates, and whatever has been typed over them.
   *
   * Derived rather than seeded in an effect, the shape `FranchiseDetailPage` settled on: the payload
   * arrives on a later render and seeding there is a cascading render.
   */
  const storedRates = useMemo(
    () => ({
      vatRateDineIn: configuration.register.dineIn ?? '',
      vatRateDelivery: configuration.register.delivery ?? '',
      defaultVatRate: configuration.register.fallback ?? '',
    }),
    [configuration.register],
  )

  /**
   * The rates this register can actually print, from the franchise's own categories - FB-070.
   *
   * `rateOptions` takes the shape `vat-rates.ts` declares, which is not the shape `/vat-categories`
   * answers - it wants `fiskalSlot` as the confirmed index. Mapped here rather than changing the
   * helper, because the helper's shape is what its 17 unit tests are written against.
   */
  const rateCategories: RateCategory[] = useMemo(
    () =>
      categories.map((category) => ({
        id: category.id,
        name: category.name,
        // `ratePercent` here, `rate` there - mapped rather than renaming either, because
        // `vat-rates.ts`'s 17 unit tests are written against its own shape.
        rate: category.ratePercent,
        letter: category.letter,
        fiskalSlot: category.fiskalSlot,
        isDefault: category.isDefault,
        isActive: category.isActive,
      })) as RateCategory[],
    [categories],
  )
  const rateChoices = useMemo(() => rateOptions(rateCategories), [rateCategories])

  if (isLoading) {
    return (
      <div className="flex justify-center py-24">
        <Spinner />
      </div>
    )
  }

  const warning = unprintableWarning(categories)
  const rates = { ...storedRates, ...(rateDraft ?? {}) }
  const ratesChanged = (Object.keys(storedRates) as (keyof typeof storedRates)[]).some(
    (key) => rates[key] !== storedRates[key],
  )

  const saveRegisterRates = () => {
    void saveRates
      .mutateAsync({ data: { ...rates } })
      .then(async () => {
        await queryClient.invalidateQueries({ queryKey: getGetVatConfigurationQueryKey() })
        await queryClient.invalidateQueries({ queryKey: getGetSettingsFiskalQueryKey() })
        setRateDraft(null)
        toast({ title: tt('Sadzby pokladne uložené') })
      })
      // §9.360: `apiErrorMessage` reads `ApiError.data`, which is where `custom-fetch` puts the
      // envelope. This read `failure.body?.error` and `ApiError` has no `body`, so the fallback was
      // the only message it could ever show.
      .catch((failure: unknown) =>
        toast({
          variant: 'destructive',
          title: apiErrorMessage(failure, tt('Uloženie zlyhalo')),
        }),
      )
  }

  const applySlot = async (row: VatSlotChannel, slot: 'dine_in' | 'delivery' | null) => {
    try {
      await setChannelSlot.mutateAsync({ id: row.id, data: slotPayload(slot) })
      await queryClient.invalidateQueries({ queryKey: getGetVatConfigurationQueryKey() })
      toast({
        title: slot === null ? tt('Sadzba sa riadi prepínačom') : tt('Sadzba kanála uložená'),
        description: row.name,
      })
    } catch (failure) {
      toast({
        variant: 'destructive',
        // §9.360, and this endpoint is one the server really does refuse in words: a slot already
        // held by another rate answers with the row that holds it.
        title: apiErrorMessage(failure, tt('Uloženie zlyhalo')),
        description: row.name,
      })
    }
  }

  return (
    <div className="space-y-5">
      <PageHeader
        title={tt('Sadzby DPH')}
        description={tt('Aké sadzby sa účtujú, a či ich pokladňa vie vytlačiť.')}
        icon={<Percent className="w-5 h-5" />}
        actions={
          <button
            type="button"
            data-add-vat
            disabled={!writable}
            onClick={() => setEditing({ id: null, form: emptyForm(categories) })}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border text-xs font-semibold hover:bg-muted/50 disabled:opacity-50"
          >
            <Plus className="w-3.5 h-3.5" />
            <T>Pridať sadzbu</T>
          </button>
        }
      />

      {readOnly !== null && (
        <p className="flex items-start gap-1.5 text-xs text-amber-500" data-read-only>
          <AlertCircle className="w-4 h-4 shrink-0 mt-px" />
          <span>{readOnly}</span>
        </p>
      )}

      {/* FB-183 - the headline, because it fails at the till and nowhere earlier. */}
      {warning !== '' && (
        <div className="rounded-2xl border border-red-500/40 bg-red-500/5 p-4 space-y-2" data-unprintable>
          <p className="flex items-start gap-1.5 text-xs text-red-500 font-semibold">
            <AlertCircle className="w-4 h-4 shrink-0 mt-px" />
            {warning}
          </p>
          <p className="text-[11px] text-muted-foreground" data-slot-help>
            {slotDiscoveryNote()}
          </p>
        </div>
      )}

      <p className="text-xs text-muted-foreground" data-default-note>
        {defaultNote(categories)}
      </p>

      {/* §9.324 - the register's fallback pair, moved here from Pokladňa → Nastavenia. That page
          still shows the values and links back, because a product with no rate of its own falls
          through to them and somebody configuring a register has a reason to see them - but this is
          the only screen that writes them. */}
      <section
        className="bg-card border border-border rounded-2xl overflow-hidden"
        data-register-rates
      >
        <div className="px-5 py-3 border-b border-border bg-muted/20">
          <h2 className="font-bold text-foreground text-sm">
            <T>Sadzby pokladne</T>
          </h2>
          <p className="text-xs text-muted-foreground mt-1" data-register-note>
            {registerNote(configuration.register)}
          </p>
        </div>
        <div className="px-5 py-4 space-y-4">
          {/* §9.327 - editable here, and **here only**. The owner asked twice: *"I am pretty sure
              you can do it only in one place, not on two places these Sadzby DPH."* §9.324 had kept
              these on *Pokladňa → Nastavenia* because moving them would have orphaned the guard
              that catches a rate banding onto another letter - so `vat-rates.ts` and the picker
              moved here with them. Nothing was deleted, which is the only version of *one place*
              worth having. */}
          <div className="grid gap-4 sm:grid-cols-2" data-register-dine-in>
            <label className="space-y-1">
              <span className="text-[11px] font-semibold text-muted-foreground">
                {slotLabel('dine_in')}
              </span>
              <RegisterRatePicker
                value={rates.vatRateDineIn}
                onChange={(value) =>
                  setRateDraft((current) => ({ ...(current ?? {}), vatRateDineIn: value }))
                }
                options={rateChoices}
                categories={rateCategories}
                label={tt('DPH - konzumácia v reštaurácii')}
                disabled={!writable}
              />
            </label>
            <label className="space-y-1" data-register-delivery>
              <span className="text-[11px] font-semibold text-muted-foreground">
                {slotLabel('delivery')}
              </span>
              <RegisterRatePicker
                value={rates.vatRateDelivery}
                onChange={(value) =>
                  setRateDraft((current) => ({ ...(current ?? {}), vatRateDelivery: value }))
                }
                options={rateChoices}
                categories={rateCategories}
                label={tt('DPH - rozvoz a odber')}
                disabled={!writable}
              />
            </label>
            <label className="space-y-1 sm:col-span-2" data-register-fallback>
              <span className="text-[11px] font-semibold text-muted-foreground">
                <T>Predvolená - použije sa, ak typ objednávky nie je určený</T>
              </span>
              <RegisterRatePicker
                value={rates.defaultVatRate}
                onChange={(value) =>
                  setRateDraft((current) => ({ ...(current ?? {}), defaultVatRate: value }))
                }
                options={rateChoices}
                categories={rateCategories}
                label={tt('DPH - predvolená sadzba')}
                disabled={!writable}
              />
            </label>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              data-save-register-rates
              disabled={!writable || !ratesChanged || saveRates.isPending}
              onClick={saveRegisterRates}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-primary/40 bg-primary/10 text-primary text-xs font-semibold disabled:opacity-40"
            >
              <Save className="w-3.5 h-3.5" /> <T>Uložiť sadzby pokladne</T>
            </button>
            {saveRates.isPending && <Spinner className="w-4 h-4" />}
            {/* A partial body: `SharedSettingsRequest.applyTo` is null-guarded per field, so these
                three go alone and the restaurant's name, address, IČO and DIČ - which share the
                endpoint - are untouched. */}
            <span className="text-[11px] text-muted-foreground" data-register-dirty>
              {ratesChanged ? tt('neuložené zmeny') : ''}
            </span>
          </div>
        </div>
      </section>

      {/* §9.324's third clause - *"make sure that I ican change in predajný kanál set if its sadzba
          DPH na mieiste or rozvoz"*. Two facts per row, because a null slot is *following the flag*
          and not a choice somebody made - and `Do ruky` is exactly where those two differ. */}
      <section
        className="bg-card border border-border rounded-2xl overflow-hidden"
        data-channel-slots={configuration.channels.length}
      >
        <div className="px-5 py-3 border-b border-border bg-muted/20">
          <h2 className="font-bold text-foreground text-sm flex items-center gap-2">
            <Truck className="w-4 h-4 text-primary" /> <T>Sadzba podľa predajného kanála</T>
          </h2>
          <p className="text-xs text-muted-foreground mt-1">
            <T>
              Ktorú z dvoch sadzieb kanál účtuje. Bez nastavenia sa použije prepínač «Na mieste».
            </T>
          </p>
        </div>
        <div className="divide-y divide-border">
          {configuration.channels.map((row) => (
            <div
              key={row.id}
              className="px-5 py-3 flex flex-wrap items-center gap-3"
              data-channel-slot={row.slug}
              data-channel-effective={row.effectiveSlot}
            >
              <span className="font-semibold text-sm text-foreground min-w-[9rem]">{row.name}</span>
              <span className="text-[11px] text-muted-foreground flex-1" data-slot-source={row.slug}>
                {slotSource(row)}
              </span>
              {(['dine_in', 'delivery'] as const).map((slot) => (
                <button
                  key={slot}
                  type="button"
                  data-set-slot={`${row.slug}:${slot}`}
                  disabled={!writable || setChannelSlot.isPending || row.vatSlot === slot}
                  onClick={() => void applySlot(row, slot)}
                  className={cn(
                    'min-h-[32px] px-3 rounded-lg border text-xs font-semibold disabled:opacity-40',
                    row.vatSlot === slot
                      ? 'border-primary/40 bg-primary/10 text-primary'
                      : 'border-border text-muted-foreground',
                  )}
                >
                  {slotLabel(slot)}
                </button>
              ))}
              {/* Clearing is its own action. A slot set by mistake has to be undoable to *follow the
                  flag*; picking the opposite would freeze a choice nobody wanted to make. */}
              <button
                type="button"
                data-clear-slot={row.slug}
                disabled={!writable || !mayClearSlot(row) || setChannelSlot.isPending}
                onClick={() => void applySlot(row, null)}
                className="flex items-center gap-1 min-h-[32px] px-2 rounded-lg border border-border text-[11px] font-semibold text-muted-foreground disabled:opacity-40"
              >
                <RotateCcw className="w-3 h-3" /> <T>Automaticky</T>
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* §9.324 - a rate that cannot be right belongs on the screen that manages rates rather than
          being findable only by query. Derived, so the panel disappears when the data is corrected.
          **A report and not a fix**: which rate replaces a 10 % moves money in opposite directions
          depending on the answer, so it is the owner's and it is banked. */}
      {configuration.unknownRateProducts.length > 0 && (
        <section
          className="rounded-2xl border border-amber-500/40 bg-amber-500/5 p-4 space-y-2"
          data-unknown-rates={configuration.unknownRateProducts.length}
        >
          <p className="flex items-start gap-1.5 text-xs text-amber-500 font-semibold">
            <AlertCircle className="w-4 h-4 shrink-0 mt-px" />
            <T>Tieto produkty účtujú sadzbu, ktorú nemáte v zozname</T>
          </p>
          <ul className="space-y-1">
            {configuration.unknownRateProducts.map((product) => (
              <li
                key={product.id}
                className="text-[11px] text-muted-foreground"
                data-unknown-rate={product.id}
              >
                <strong className="text-foreground">{product.name}</strong> - {unknownRateNote(product)}
                {!product.active && (
                  <span className="ml-1">
                    (<T>neaktívny</T>)
                  </span>
                )}
              </li>
            ))}
          </ul>
          <p className="text-[11px] text-muted-foreground" data-unknown-rate-help>
            <T>
              Sadzbu treba opraviť v produkte alebo pridať do zoznamu. Kým sa to nestane, pokladňa
              nevie, ktorý index má vytlačiť.
            </T>
          </p>
        </section>
      )}

      <div className="space-y-2" data-vat-categories={categories.length}>
        {categories.map((category) => (
          <div
            key={category.id}
            className={cn(
              'rounded-2xl border p-4 flex flex-wrap items-center gap-3',
              category.isActive ? 'border-border bg-card' : 'border-border bg-muted/30',
              category.isActive && !isPrintable(category) && 'border-red-500/40',
            )}
            data-vat={category.letter}
            data-vat-printable={isPrintable(category) ? 'yes' : 'no'}
          >
            <span className="w-9 h-9 rounded-xl bg-primary/10 text-primary font-black flex items-center justify-center shrink-0">
              {category.letter}
            </span>
            <div className="min-w-0 flex-1">
              <p className="font-bold text-sm flex items-center gap-2">
                {formatDecimal(category.ratePercent, 2)} %
                <span className="font-normal text-muted-foreground truncate">{category.name}</span>
                {category.isDefault && <StatusBadge label={tt('základná')} color="emerald" />}
                {!category.isActive && <StatusBadge label={tt('vypnutá')} color="zinc" />}
              </p>
              <p
                className={cn(
                  'text-[11px] mt-0.5',
                  isPrintable(category) ? 'text-muted-foreground' : 'text-red-500',
                )}
                data-printability
              >
                {printabilityNote(category)}
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                data-edit-vat={category.letter}
                disabled={!writable}
                onClick={() => setEditing({ id: category.id, form: formOf(category) })}
                className="px-3 py-1.5 rounded-lg border border-border text-xs font-semibold disabled:opacity-50"
              >
                <T>Upraviť</T>
              </button>
              <button
                type="button"
                data-delete-vat={category.letter}
                disabled={!writable}
                onClick={() => setDeleting(category)}
                className="px-3 py-1.5 rounded-lg border border-border text-xs font-semibold text-red-500 disabled:opacity-50 flex items-center gap-1.5"
              >
                <Trash2 className="w-3 h-3" />
                <T>Zmazať</T>
              </button>
            </div>
          </div>
        ))}
      </div>

      {editing !== null && (
        <VatDialog
          form={editing.form}
          editingId={editing.id}
          others={categories.filter((category) => category.id !== editing.id)}
          saving={create.isPending || update.isPending}
          onChange={(form) => setEditing({ ...editing, form })}
          onClose={() => setEditing(null)}
          onSave={() => {
            const payload = vatPayload(editing.form)
            if (editing.id === null) create.mutate({ data: payload })
            else update.mutate({ id: editing.id, data: payload })
          }}
        />
      )}

      <ConfirmDialog
        open={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={() => deleting !== null && remove.mutate({ id: deleting.id })}
        title={deleting === null ? '' : `${tt('Zmazať sadzbu')} ${deleting.letter}?`}
        description={deleting === null ? '' : deleteWarning(deleting)}
        confirmLabel={tt('Zmazať')}
        variant="danger"
        loading={remove.isPending}
      />
    </div>
  )
}

function VatDialog({
  form,
  editingId,
  others,
  saving,
  onChange,
  onClose,
  onSave,
}: {
  form: VatForm
  editingId: number | null
  others: VatCategory[]
  saving: boolean
  onChange: (form: VatForm) => void
  onClose: () => void
  onSave: () => void
}) {
  const problems = problemsOf(form, others)

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={editingId === null ? tt('Nová sadzba') : tt('Upraviť sadzbu')}
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-card border border-border rounded-2xl w-full max-w-md overflow-hidden flex flex-col shadow-2xl"
        onClick={(event) => event.stopPropagation()}
        data-vat-dialog
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h3 className="font-bold text-sm">
            {editingId === null ? tt('Nová sadzba') : `${tt('Upraviť')} ${form.letter}`}
          </h3>
          <button type="button" onClick={onClose} aria-label={tt('Zatvoriť')} className="p-1.5 rounded-lg hover:bg-muted">
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>

        <div className="p-5 space-y-3">
          <Field label={tt('Názov')}>
            <input
              value={form.name}
              onChange={(event) => onChange({ ...form, name: event.target.value })}
              aria-label={tt('Názov')}
              className={INPUT}
            />
          </Field>
          <div className="grid grid-cols-3 gap-3">
            <Field label={tt('Písmeno')}>
              <input
                value={form.letter}
                onChange={(event) => onChange({ ...form, letter: event.target.value })}
                aria-label={tt('Písmeno')}
                maxLength={1}
                className={cn(INPUT, 'font-mono uppercase')}
              />
            </Field>
            <Field label={tt('Sadzba %')}>
              <input
                value={form.rate}
                onChange={(event) => onChange({ ...form, rate: event.target.value })}
                aria-label={tt('Sadzba')}
                className={cn(INPUT, 'tabular-nums')}
              />
            </Field>
            <Field label={tt('Index pokladne')}>
              <input
                value={form.fiskalSlot}
                onChange={(event) => onChange({ ...form, fiskalSlot: event.target.value })}
                aria-label={tt('Index pokladne')}
                data-vat-slot
                className={cn(INPUT, 'tabular-nums')}
              />
            </Field>
          </div>

          <p className="text-[11px] text-muted-foreground">{slotDiscoveryNote()}</p>

          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(event) => onChange({ ...form, isActive: event.target.checked })}
              aria-label={tt('Aktívna')}
              className="w-4 h-4 rounded accent-primary"
            />
            <T>Aktívna - vypnutú sadzbu nemožno priradiť novému produktu</T>
          </label>

          {problems.length > 0 && (
            <div className="space-y-1" data-vat-problems>
              {problems.map((problem) => (
                <p
                  key={problem.kind}
                  className={cn(
                    'flex items-start gap-1.5 text-xs',
                    isBlocking(problem) ? 'text-red-500' : 'text-amber-500',
                  )}
                >
                  <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-px" />
                  {problemMessage(problem)}
                </p>
              ))}
            </div>
          )}
        </div>

        <div className="flex gap-3 px-5 py-4 border-t border-border">
          <button
            type="button"
            onClick={onClose}
            className={cn(BUTTON_SECONDARY, 'flex-1 px-4 py-2.5 rounded-xl text-sm')}
          >
            <T>Zrušiť</T>
          </button>
          <button
            type="button"
            data-save-vat
            onClick={onSave}
            disabled={saving || !canSave(form, others)}
            className={cn('flex-1 px-4 py-2.5 rounded-xl', BUTTON_PRIMARY, 'text-sm')}
          >
            {saving ? <Spinner className="w-4 h-4 mx-auto" /> : <T>Uložiť</T>}
          </button>
        </div>
      </div>
    </div>
  )
}

const INPUT =
  'w-full bg-background border border-border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/20'

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <span className="text-xs text-muted-foreground uppercase tracking-wide font-medium block">
        {label}
      </span>
      {children}
    </div>
  )
}
